/**
 * Which entries of a list's SOURCE apollo config are filters (PREC-4622).
 *
 * The config is one JSON blob several steps write. The Filter step's filters sit beside settings
 * other steps own (`maxPerCompany`, `firstSaved`, the Find person waterfall
 * `enrichmentFilterConfig`, `findPersonMode`, the tracker's `findPersonOnSignal`) and, on lists
 * from 2024, the Apollo people search's own parameters (`q_keywords`, `useStartups`, …), which
 * only that search reads. So a reader decides by an allow-list of what a filter IS, never by a
 * list of what to skip: the skip list was three keys long when PREC-4400 added a fourth setting,
 * and every person found on a list from the new wizard ended "Not found".
 *
 * Shared because three readers must agree: the API's filter run (which applies the filters), the
 * API's config merge (a Filter step save replaces the filters and keeps every setting) and the
 * web's Filter step (which shows the filters and saves them back).
 */

/** How the filter run reads the stored value of a {@link LEGACY_APOLLO_FILTERS} key. */
export type LegacyApolloValueSource =
	/** `value` of a condition, else the stored array itself; the type defaults. */
	| "valueOrSelf"
	/** The stored value itself, whatever its shape (person_titles has always been read so). */
	| "self"
	/** `value` and `type` of a condition; only the field is named here. */
	| "conditionValue"
	/** The first "from,till" range of an array, or the "from,till" string the Filter step writes. */
	| "employeeRange";

/** A key the filter run maps onto a field itself, whatever the stored entry says. */
export interface LegacyApolloFilter {
	filterParam: string;
	valueSource: LegacyApolloValueSource;
	defaultType?: string;
	/** The Filter step's row for this key (its filter definition's name). */
	filterName: string;
}

/**
 * The keys written before filters named their own field (the Apollo search and the first Filter
 * step), with the field each is applied to. The one table both the filter run and the web read.
 */
export const LEGACY_APOLLO_FILTERS: Readonly<Record<string, LegacyApolloFilter>> = {
	seniority: {
		filterParam: "prospect.seniority",
		valueSource: "valueOrSelf",
		defaultType: "contains",
		filterName: "seniority",
	},
	person_locations: {
		filterParam: "prospect.country",
		valueSource: "valueOrSelf",
		defaultType: "contains",
		filterName: "country",
	},
	person_titles: { filterParam: "prospect.title", valueSource: "self", filterName: "title" },
	organization_locations: {
		filterParam: "organization.country",
		valueSource: "valueOrSelf",
		defaultType: "contains",
		filterName: "companyCountry",
	},
	industries: {
		filterParam: "organization.industry",
		valueSource: "valueOrSelf",
		defaultType: "contains",
		filterName: "industry",
	},
	organization_num_employees_ranges: {
		filterParam: "organization.estimatedNumEmployees",
		valueSource: "employeeRange",
		defaultType: "between",
		filterName: "estimatedNumEmployees",
	},
	countryBranch: {
		filterParam: "companyLocations.country",
		valueSource: "conditionValue",
		filterName: "countryBranch",
	},
	stateBranch: {
		filterParam: "companyLocations.state",
		valueSource: "conditionValue",
		filterName: "stateBranch",
	},
	cityBranch: {
		filterParam: "companyLocations.city",
		valueSource: "conditionValue",
		filterName: "cityBranch",
	},
};

/** The table entry of a key, or undefined; never a property inherited from Object. */
export function legacyApolloFilterOf(key: string): LegacyApolloFilter | undefined {
	return Object.prototype.hasOwnProperty.call(LEGACY_APOLLO_FILTERS, key)
		? LEGACY_APOLLO_FILTERS[key]
		: undefined;
}

/**
 * - `filter`: applied by the filter run, shown and saved by the Filter step;
 * - `unreadable`: shaped like a filter but nothing can be applied from it; it belongs to the
 *   Filter step, and the filter run lets nobody past it (an unreadable restriction never widens
 *   what passes);
 * - `setting`: anything else; a Filter step save keeps it.
 */
export type ApolloConfigEntryKind = "filter" | "unreadable" | "setting";

function isPlainObject(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** A condition as the Filter step stores it: an object that names the field it filters on. */
export function isApolloFilterCondition(
	value: unknown
): value is { filterParam: string; type?: string; value?: unknown; customColumnId?: number } {
	return (
		isPlainObject(value) && typeof value.filterParam === "string" && value.filterParam.length > 0
	);
}

/** A range's bounds; either end may be open. */
export interface RangeBounds {
	from?: string | number;
	till?: string | number;
}

const isBlank = (bound: unknown) => bound === undefined || bound === null || bound === "";

/**
 * The bounds of a stored employee range, or null when none is set (no string, blank, or both ends
 * empty). The Apollo search stores an array of "from,till" strings (the first one counts, as it
 * always did at the company stage); the Filter step stored one string.
 */
export function employeeRangeBoundsOf(raw: unknown): { from?: string; till?: string } | null {
	const range = Array.isArray(raw) ? raw[0] : raw;

	if (typeof range !== "string") {
		return null;
	}

	const [from, till] = range.split(",").map((bound) => bound.trim());

	return from || till ? { from: from || undefined, till: till || undefined } : null;
}

/** True when the stored range is a string (or an array of them) that sets no bound. */
function isEmptyEmployeeRange(raw: unknown): boolean {
	const range = Array.isArray(raw) ? raw[0] : raw;

	return (Array.isArray(raw) && raw.length === 0) || typeof range === "string";
}

/**
 * Whether a count lies in a range, both bounds included and an open end unbounded. A missing or
 * non-numeric count is outside every range: the person stage files it as "value missing", and the
 * company stage must not let a company of unknown size through either (PREC-4622).
 */
export function isWithinBounds(count: unknown, bounds: RangeBounds): boolean {
	if (isBlank(count) || Number.isNaN(Number(count))) {
		return false;
	}

	const n = Number(count);

	return (
		(isBlank(bounds?.from) || n >= Number(bounds.from)) &&
		(isBlank(bounds?.till) || n <= Number(bounds.till))
	);
}

/** A range in words, for an exclusion reason: "11 to 50", "10001 or more", "up to 50". */
export function describeBounds(bounds: RangeBounds): string {
	const from = isBlank(bounds?.from) ? undefined : bounds.from;
	const till = isBlank(bounds?.till) ? undefined : bounds.till;

	if (from !== undefined && till !== undefined) {
		return `${from} to ${till}`;
	}

	if (from !== undefined) {
		return `${from} or more`;
	}

	return till !== undefined ? `up to ${till}` : "any number";
}

const optionText = (option: unknown): string => {
	const text =
		typeof option === "object" && option !== null
			? ((option as { title?: unknown; name?: unknown }).title ??
				(option as { name?: unknown }).name)
			: option;

	return String(text ?? "")
		.trim()
		.toLowerCase();
};

/**
 * Whether a value passes an include or exclude list the way the person stage compares one: any
 * case, options as strings or `{ title }` / `{ name }`. Null for a type this helper does not
 * decide (the person stage applies it).
 */
export function matchesOptionFilter(
	type: string | undefined,
	options: unknown,
	actual: unknown
): boolean | null {
	const listed = (Array.isArray(options) ? options : []).map(optionText);
	const found = listed.includes(optionText(actual));

	if (type === "contains" || type === "dropdown") {
		return found;
	}

	return type === "dropdownExclude" ? !found : null;
}

/** A legacy key's stored value as the filter run reads it: the field, the type and the value. */
export function resolveLegacyApolloFilter(
	key: string,
	raw: unknown
): { filterParam: string; type: string | undefined; value: unknown } | null {
	const legacy = legacyApolloFilterOf(key);

	if (!legacy) {
		return null;
	}

	const condition = (isPlainObject(raw) ? raw : {}) as { type?: string; value?: unknown };
	const { filterParam } = legacy;

	switch (legacy.valueSource) {
		case "valueOrSelf":
			return {
				filterParam,
				value: condition.value || raw,
				type: condition.type || legacy.defaultType,
			};
		case "self":
			return { filterParam, value: raw, type: condition.type };
		case "conditionValue":
			return { filterParam, value: condition.value, type: condition.type };
		case "employeeRange":
			return { filterParam, value: employeeRangeBoundsOf(raw) ?? {}, type: legacy.defaultType };
	}
}

function legacyEntryKind(legacy: LegacyApolloFilter, value: unknown): ApolloConfigEntryKind {
	if (legacy.valueSource !== "employeeRange") {
		if (Array.isArray(value)) {
			return "filter";
		}

		// `{}` names nothing to compare, and the run would throw on it ("Not found" for every
		// row): fail closed, as for any filter we cannot read (PREC-4622).
		return isPlainObject(value) && ["type", "value", "filterParam"].some((field) => field in value)
			? "filter"
			: "unreadable";
	}

	if (isApolloFilterCondition(value) || employeeRangeBoundsOf(value)) {
		return "filter";
	}

	return isEmptyEmployeeRange(value) ? "setting" : "unreadable";
}

/** What one entry of an apollo config is. */
export function apolloConfigEntryKind(key: string, value: unknown): ApolloConfigEntryKind {
	if (value === null || value === undefined) {
		return "setting";
	}

	const legacy = legacyApolloFilterOf(key);

	if (legacy) {
		return legacyEntryKind(legacy, value);
	}

	if (isApolloFilterCondition(value)) {
		return "filter";
	}

	return isPlainObject(value) && typeof value.type === "string" ? "unreadable" : "setting";
}

/** The entries of one kind, e.g. the unreadable ones the Filter step keeps and shows. */
export function apolloConfigEntriesOfKind(
	config: unknown,
	kind: ApolloConfigEntryKind
): Record<string, unknown> {
	if (!isPlainObject(config)) {
		return {};
	}

	return Object.fromEntries(
		Object.entries(config).filter(([key, value]) => apolloConfigEntryKind(key, value) === kind)
	);
}

/** True while any entry of the config is a filter nothing can be read from. */
export function hasUnreadableApolloEntry(config: unknown): boolean {
	return Object.keys(apolloConfigEntriesOfKind(config, "unreadable")).length > 0;
}
